
import React, { useState, useEffect } from 'react';
import { useTheme } from '@material-ui/core';
import { getDomain, Domain, deleteDomain, addDomain, updateDomain } from 'utils/DomainUtils';
import MaterialTable from 'material-table';
import withAppStylesGrid from 'HOCs/withAppStyles';
import { useAppContext } from 'hooks/useAppContext';
import CenteredError from 'components/Shared/CenteredError';
import CenteredCircularProgress from 'components/Shared/CenteredCircularProgress';
import { NotificationsSnackBar } from 'components/Shared/DynamicForm';

const DomainsDataGrid = ({ values, domains }) => {
    const domainClass = domains[values.domain];
    const theme = useTheme();
    const { selectedProject } = useAppContext();
    const [data, setData] = useState({ success: undefined, data: [], domain: undefined });
    const [updateDate, setUpdateDate] = useState(new Date().toString());
    const [editStatus, setEditStatus] = useState({ success: undefined, alertType: undefined, message: "" });

    useEffect(() => {
        let mounted = true;
        const getGridData = async () => {
            const data = await getDomain(domainClass, selectedProject);
            if (data.success) {
                mounted && setData(data);
            } else {
                setData(old => ({
                    ...old,
                    success: false
                }));
            }
        };
        setData(old => ({ ...old, success: undefined }));
        getGridData();
        return function cleanUp() { mounted = false }
    }, [domainClass, updateDate, selectedProject]);

    const WrappedGrid = withAppStylesGrid(MaterialTable, { getRecords: data.data, theme });

    const handleSuccess = (message = "") => setEditStatus({ success: true, alertType: "success", message: message });
    const handleError = (message = "") => setEditStatus({ success: false, alertType: "error", message: message });

    const handleAdd = async (newData) => {
        const res = await addDomain(domainClass.dbTable, newData.description, selectedProject);
        await (() => new Promise(resolve => setTimeout(resolve, 1000)))();
        if (res.success) {
            handleSuccess("Value successfully added.");
            setUpdateDate(new Date().toString());
        } else {
            let msg = "An error has occurred. "
            handleError(res.details ? msg += res.details.message : msg);
        }
        return res;
    };

    const handleDelete = async (rowData) => {
        const res = await deleteDomain(rowData.domainId);
        await (() => new Promise(resolve => setTimeout(resolve, 1000)))();
        if (res.success) {
            handleSuccess("Value successfully updated");
            setUpdateDate(new Date().toString());
        } else {
            let msg = "An error has occurred. "
            handleError(res.details ? msg += res.details.message : msg);
        };
        return res;
    };

    const handleUpdate = async (rowData) => {
        const res = await updateDomain(rowData.domainId, rowData.description, rowData.isActive, selectedProject);
        await (() => new Promise(resolve => setTimeout(resolve, 1000)))();
        if (res.success) {
            handleSuccess("Value successfully updated");
            setUpdateDate(new Date().toString());
        } else {
            let msg = "An error has occurred. "
            handleError(res.details ? msg += res.details.message : msg);
        };

        return res;
    }

    return data.success ?
        <>
            <WrappedGrid
                key={updateDate}
                columns={Domain.columns}
                data={data.data}
                title={domainClass.label}
                style={{ boxShadow: "0px 0px" }}
                options={{ pageSizeOptions: [10, 20, 50], pageSize: 10 }}
                editable={{
                    onRowDelete: handleDelete,
                    onRowAdd: handleAdd,
                    onRowUpdate: handleUpdate
                }}
                localization={{
                    body: { editRow: { deleteText: "Are you sure you'd like to mark this row as inactive?" } }
                }}
            />
            <NotificationsSnackBar status={editStatus} setStatus={(status) => setEditStatus(old => ({ ...old, success: status }))} refreshLagTime={3000} />
        </> :
        data.success === false ? <CenteredError refresh={() => setUpdateDate(new Date())} /> : <CenteredCircularProgress />
}

export default DomainsDataGrid;